import React from 'react';
import cx from 'classnames';

import { RaptorComponentHelpers } from 'pages/private/software/raptor/components/helpers';
import { BasicCard, Icon, IconName, RowLayout, Skeleton, Typography, useModal } from 'ui-lib';
import {
    IRaptorComponentCompareItem,
    IRaptorComponentCompareVersion,
    IRaptorComponentCompareState,
    RaptorComponentCompareStatus,
} from '../../deployment-create.types';

import deploymentCss from '../../../deployments.css';

interface ICompareComponentsStepProps {
    accountId: number;
    comparison?: IRaptorComponentCompareState;
    loading: boolean;
}

const ICON_NAMES: Partial<Record<RaptorComponentCompareStatus, IconName>> = {
    [RaptorComponentCompareStatus.ADD]: 'plus',
    [RaptorComponentCompareStatus.REMOVE]: 'minus',
};

const BlankComponent = (props: { className: string }): JSX.Element => (
    <span className={props.className}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
);

const buildVersionText = (
    componentVersion: IRaptorComponentCompareVersion,
    label: string,
    onClick: (componentVersionUuid: string) => void,
): JSX.Element => {
    if (!componentVersion.componentVersionUuid) return <div>{label}</div>;

    return (
        <Typography.Link onClick={() => onClick(componentVersion.componentVersionUuid)}>
            {label}
        </Typography.Link>
    );
};

const buildItemColComponent = (
    key: string,
    item: IRaptorComponentCompareItem,
    onVersionClick: (componentVersionUuid: string) => void,
): JSX.Element => {
    const className = cx(deploymentCss.item, deploymentCss[item.status]);
    const icon = ICON_NAMES[item.status];
    function buildComponentVersionLabel(item: IRaptorComponentCompareItem): string {
        if (!item.componentVersion) return '';
        return `${item.componentVersion.componentName} (${item.componentVersion.version ?? 'unknown'})`;
    }

    const label = buildComponentVersionLabel(item);

    return (
        <div key={key}>
            <div className={className}>
                {icon && label && <Icon className={deploymentCss.icon} iconName={icon} size='SMALL' />}
                {item.componentVersion && label
                    ? buildVersionText(item.componentVersion, label, onVersionClick)
                    : <div>{'\u00A0'}</div>}
            </div>
        </div>
    );
};

export const CompareComponentsStep = (props: ICompareComponentsStepProps): JSX.Element => {
    const [modal, contextHolder] = useModal();
    const childCols = [11, 1, 11];
    const comparison = props.comparison;
    const showComponentVersionDetails = (componentVersionUuid: string): void => {
        RaptorComponentHelpers.showComponentVersionDetailsModal(modal, {
            accountId: props.accountId,
            componentVersionUuid,
        });
    };

    return (
        <React.Fragment>
            {contextHolder}
            <BasicCard
                title='Compare components'
                contents={
                    <Skeleton loading={props.loading}>
                        <div key='title' className={deploymentCss.compareSettings}>
                            <RowLayout
                                className={deploymentCss.title}
                                childItems={[
                                    <h3 key='title-target'>{`Target state: ${comparison?.target.label || '-'}`}</h3>,
                                    <div key='none'></div>,
                                    <h3 key='title-current'>{`Current state: ${comparison?.current.label || '-'}`}</h3>,
                                ]}
                                childCols={childCols}
                            />
                        </div>
                        <div key='compare-data' className={deploymentCss.compareSettings}>
                            <div className={deploymentCss.compareContent}>
                                {comparison?.target.items.map((targetItem, index) => {
                                    const currentItem = comparison.current.items[index];
                                    return (
                                        <div key={index}>
                                            <RowLayout
                                                className={deploymentCss.row}
                                                childItems={[
                                                    buildItemColComponent(
                                                        `target-${index}`,
                                                        targetItem,
                                                        showComponentVersionDetails,
                                                    ),
                                                    <div key={`number-${index}`} className={deploymentCss.number}>
                                                        {index + 1}
                                                    </div>,
                                                    buildItemColComponent(
                                                        `current-${index}`,
                                                        currentItem,
                                                        showComponentVersionDetails,
                                                    ),
                                                ]}
                                                childCols={childCols}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Skeleton>
                }
                footers={
                    <div key='footer' className={deploymentCss.compareSettings}>
                        <h3>Description</h3>
                        <div key='add' className={deploymentCss.footer}>
                            <BlankComponent className={cx(deploymentCss.text, deploymentCss.addIcon)} />
                            <div>Added component</div>
                        </div>
                        <div key='remove' className={deploymentCss.footer}>
                            <BlankComponent className={cx(deploymentCss.text, deploymentCss.removeIcon)} />
                            <div>Not selected in this deployment</div>
                        </div>
                        <div key='change' className={deploymentCss.footer}>
                            <BlankComponent className={cx(deploymentCss.text, deploymentCss.changeIcon)} />
                            <div>Updated component</div>
                        </div>
                        <div key='noChange' className={deploymentCss.footer}>
                            <BlankComponent className={cx(deploymentCss.text, deploymentCss.noChangeIcon)} />
                            <div>Unchanged component</div>
                        </div>
                    </div>
                }
            />
        </React.Fragment>
    );
};
